// @ts-strict-ignore
import React, { useEffect } from 'react';

import { PageContentBlock } from 'api/cms';
import { PublicPage } from 'api/website';
import {
  getContentHasBackground,
  getIsContentFullWidthLayout,
} from 'cms/helpers';
import { useContentSubsite } from 'cms/hooks';
import { Box } from 'components/Box';
import { SecondaryButton } from 'components/Button';
import { CmsSocialButtons } from 'components/CmsSocialButtons';
import SubscribeToSubsiteContainer from 'containers/SubscribeToSubsiteContainer';
import { trackClicked, trackPageView } from 'utils/analytics';
import { isNotEmpty } from 'utils/functional';

import { ContentPageBelowBlocks } from './ContentPageBelowBlocks';
import { ContentPageBody } from './ContentPageBody';
import { ContentPageHeaderImage } from './ContentPageHeaderImage';
import { ContentPageLowerSidebar } from './ContentPageLowerSidebar';
import { ContentPageSubscribed } from './ContentPageSubscribed';
import { ContentPageUpperSidebar } from './ContentPageUpperSidebar';

type Props = {
  content: PublicPage;
};

export function ContentPageWrapper({ content }: Props) {
  const fullWidthLayout = getIsContentFullWidthLayout({ content });
  const hasBelowBlocks =
    content.hasRecommendedJobs ||
    (content.tags && content.tags.length > 0) ||
    content.author;

  const subsite = useContentSubsite({ content });

  useEffect(() => {
    trackPageView('CMS Page', {
      subsite_name: subsite.publicName,
      page_title: content.title,
      page_type: content.type,
      categories: content.categories.map((category) => category.name),
    });
  }, [content, subsite.publicName]);

  const firstBlock: PageContentBlock | undefined = content.contentBlocks[0];
  const lastBlock = content.contentBlocks[content.contentBlocks.length - 1] || {
    type: undefined,
  };

  return (
    <ContentPageBody
      content={content}
      padTop={
        isNotEmpty(content.contentBlocks) &&
        !(
          [
            'STICKY_HEADER_LINKS',
            'STICKY_HEADER_LINKS_GRAD',
            'CTA_HERO',
            'GRAD_CTA_HERO',
            'DONATE',
            'SIGN_UP',
            'SCROLL_TRACKING',
          ].includes(firstBlock.type) ||
          (firstBlock.type === 'SPLIT_CONTENT' &&
            ['FULL_WIDTH', 'OVERLAPPING'].includes(
              firstBlock.data.appearance
            )) ||
          getContentHasBackground({ block: firstBlock, fullWidthLayout })
        )
      }
      padBottom={
        !['CTA_HERO', 'GRAD_CTA_HERO', 'SPLIT_CONTENT'].includes(lastBlock.type)
      }
      headerImage={
        content.headerImage && <ContentPageHeaderImage content={content} />
      }
      socialButtons={
        content.hasSocialSharing && (
          <CmsSocialButtons
            content={content}
            message={content.metaDescription}
            title={content.title}
            subsite={subsite.publicName}
          />
        )
      }
      subscribeButton={
        subsite.canSubscribe &&
        content.type === 'POST' && (
          <Box m="6px">
            {/* @ts-expect-error ts-migrate(2786) FIXME: 'SubscribeToSubsiteContainer' cannot be used as a ... Remove this comment to see the full error message */}
            <SubscribeToSubsiteContainer
              subsiteId={subsite.id}
              render={({ subscribe, isSubscribed, submitting }) => (
                <Box
                  component={SecondaryButton}
                  classes={['flex', 'align-center', 'justify-center']}
                  height={32}
                  minWidth={132}
                  size="small"
                  onClick={() => {
                    subscribe();
                    trackClicked('Subscribe', {
                      context: 'CMS Page',
                    });
                  }}
                  data-qa-id="subscribe-to-subsite"
                  disabled={isSubscribed}
                >
                  <ContentPageSubscribed
                    submitting={submitting}
                    isSubscribed={isSubscribed}
                  />
                </Box>
              )}
            />
          </Box>
        )
      }
      belowBlocks={
        hasBelowBlocks ? <ContentPageBelowBlocks content={content} /> : null
      }
      upperSidebar={
        content.sidebar && content.sidebar.contentBlocksTop.length ? (
          <ContentPageUpperSidebar content={content} />
        ) : null
      }
      lowerSidebar={
        content.sidebar && content.sidebar.contentBlocksBottom.length > 0 ? (
          <ContentPageLowerSidebar content={content} />
        ) : null
      }
    />
  );
}
